import { parsePositiveCentreId } from 'app/shared/api/getCentres';
import { apiTaxDateToDayjs, type TaxRuleListRow } from './types';

function normalizeTaxListCountryId(v: unknown): number | string | undefined {
  if (v == null) return undefined;
  if (typeof v === 'number') {
    return Number.isFinite(v) && v > 0 ? v : undefined;
  }
  const s = String(v).trim();
  if (s === '') return undefined;
  const n = Number(s);
  if (Number.isFinite(n) && n > 0 && String(n) === s) return n;
  return s;
}

function pickListCentreId(r: Record<string, unknown>): number | undefined {
  const flat =
    r.centreId ??
    r.CentreId ??
    r.centreID ??
    r.CentreID ??
    r.centerId ??
    r.CenterId ??
    r.centre_id ??
    r.Centre_Id ??
    r.center_id ??
    r.numericCentreId ??
    r.NumericCentreId;
  const fromFlat = parsePositiveCentreId(flat);
  if (fromFlat != null) return fromFlat;

  const centreObj = r.centre ?? r.Centre;
  if (centreObj && typeof centreObj === 'object') {
    const o = centreObj as Record<string, unknown>;
    const nested = o.id ?? o.Id ?? o.centreId ?? o.CentreId;
    return parsePositiveCentreId(nested);
  }
  return undefined;
}

function hasPositiveNumericCentreId(raw: Record<string, unknown>): boolean {
  return pickListCentreId(raw) != null;
}

function extractScopeLabels(raw: Record<string, unknown>): string[] | undefined {
  const cn = raw.centreNames ?? raw.CentreNames;
  if (Array.isArray(cn)) {
    const labels = cn.map((x) => String(x)).filter(Boolean);
    if (labels.length) return labels;
  }
  const centresArr = raw.centres ?? raw.Centres;
  if (Array.isArray(centresArr)) {
    const names = centresArr
      .map((c: Record<string, unknown>) => String(c?.name ?? c?.Name ?? ''))
      .filter(Boolean);
    if (names.length) return names;
  }
  const centreObjEarly = raw.centre ?? raw.Centre;
  if (centreObjEarly && typeof centreObjEarly === 'object') {
    const o = centreObjEarly as Record<string, unknown>;
    const nm = o.name ?? o.Name;
    if (nm != null && String(nm).trim()) {
      return [String(nm).trim()];
    }
  }
  if (hasPositiveNumericCentreId(raw)) {
    const singleCn = raw.centreName ?? raw.CentreName;
    if (singleCn != null && String(singleCn).trim()) {
      return [String(singleCn).trim()];
    }
  }
  const countryName = raw.countryName ?? raw.CountryName;
  if (countryName != null && String(countryName).trim()) {
    return [String(countryName)];
  }
  return undefined;
}

export function mapTaxRuleListItem(raw: unknown): TaxRuleListRow {
  const r = raw as Record<string, unknown>;
  const isoRaw = r.countryIsoCode ?? r.CountryIsoCode;
  const hasIso = isoRaw != null && String(isoRaw).trim() !== '';
  return {
    id: Number(r.id) || 0,
    name: String(r.name ?? ''),
    startDate: apiTaxDateToDayjs(r.startDate),
    endDate: apiTaxDateToDayjs(r.endDate),
    isActive: r.isActive !== false,
    countryId: normalizeTaxListCountryId(hasIso ? isoRaw : (r.countryId ?? r.CountryId)),
    centreId: pickListCentreId(r),
    lastModifiedBy: r.lastModifiedBy != null ? String(r.lastModifiedBy) : undefined,
    lastModifiedBySurname: r.lastModifiedBySurname != null ? String(r.lastModifiedBySurname) : undefined,
    lastModifiedByGivenName: r.lastModifiedByGivenName != null ? String(r.lastModifiedByGivenName) : undefined,
    scopeLabelsFromApi: extractScopeLabels(r),
  };
}